import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import * as api from '../services/api';
import { getPatientUuid } from '../utils/auth';

// ─── Styles ──────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 8px 0;
`;

const Empty = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #e5e7eb;
`;

const LoadingText = styled.div`
  padding: 32px;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: middle;
`;

const FileName = styled.span`
  font-weight: 500;
`;

const DateText = styled.span`
  color: #64748b;
  font-size: 0.8125rem;
`;

const ViewBtn = styled.button`
  padding: 4px 12px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #dbeafe; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const PdfBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const PdfFrame = styled.div`
  width: min(960px, 96vw);
  height: min(88vh, 900px);
  background: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
`;

const PdfHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #1e293b;
`;

const PdfTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PdfClose = styled.button`
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #94a3b8;
  background: transparent;
  border: 1px solid #334155;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { color: #e2e8f0; background: #334155; }
`;

const PdfIframe = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: #fff;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s: string | null) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return s; }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  title?: string;
}

const PatientAttachmentsView: React.FC<Props> = ({ title = 'My Records' }) => {
  const [attachments, setAttachments] = useState<api.PatientAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string>('');

  const patientUuid = getPatientUuid();

  const load = useCallback(async () => {
    if (!patientUuid) { setLoading(false); return; }
    try {
      const res = await api.getPatientAttachments(patientUuid);
      setAttachments(res?.attachments ?? []);
    } catch {
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [patientUuid]);

  useEffect(() => { load(); }, [load]);

  const handleView = async (attachment: api.PatientAttachment) => {
    setOpeningId(attachment.id);
    try {
      const { url, file_name } = await api.getAttachmentPresignedUrl(attachment.id, 'view');
      setPdfName(file_name || attachment.file_name);
      setPdfUrl(url);
    } catch {
      // silently ignore
    } finally {
      setOpeningId(null);
    }
  };

  const closePdf = useCallback(() => setPdfUrl(null), []);

  if (loading) return <LoadingText>Loading records…</LoadingText>;

  if (!patientUuid || attachments.length === 0) {
    return (
      <Container>
        <Empty>No records on file yet. Please contact your provider if you expect to see documents here.</Empty>
      </Container>
    );
  }

  return (
    <Container>
      {title && (
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: '0 0 14px 0' }}>
          {title}
        </h2>
      )}
      <Table>
        <thead>
          <tr>
            <Th>File</Th>
            <Th>Date</Th>
            <Th>Size</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {attachments.map(a => (
            <tr key={a.id}>
              <Td><FileName>{a.file_name}</FileName></Td>
              <Td><DateText>{fmtDate(a.attached_date || a.created_at)}</DateText></Td>
              <Td><DateText>{fmtBytes(a.file_size)}</DateText></Td>
              <Td>
                <ViewBtn
                  disabled={openingId === a.id}
                  onClick={() => handleView(a)}
                >
                  {openingId === a.id ? 'Opening…' : 'View'}
                </ViewBtn>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {pdfUrl && (
        <PdfBackdrop onClick={closePdf}>
          <PdfFrame onClick={e => e.stopPropagation()}>
            <PdfHeader>
              <PdfTitle>{pdfName}</PdfTitle>
              <PdfClose onClick={closePdf}>Close</PdfClose>
            </PdfHeader>
            <PdfIframe title={pdfName} src={pdfUrl} />
          </PdfFrame>
        </PdfBackdrop>
      )}
    </Container>
  );
};

export default PatientAttachmentsView;
